import { useState } from 'react';
import Api from 'api';
import { getCountryCode, useLang } from 'utils/localisation';
import { useDefferedCallback } from 'utils/hooks';

const getInitialCoordinates = (coordinates, initialZoomLevel) => {
  const { LatLng } = window.google.maps;

  if (coordinates) {
    return {
      center: new LatLng({ lat: Number(coordinates[0]), lng: Number(coordinates[1]) }),
      zoom: initialZoomLevel,
    };
  }

  return {
    center: new LatLng({ lat: 50.07, lng: 19.94 }),
    zoom: initialZoomLevel,
  };
};

export const initMap = (wrapper, initialCoordinates, mapOptions, initialZoomLevel) => {
  const { Map: MapInstance } = window.google.maps;
  const options = { ...mapOptions, ...getInitialCoordinates(initialCoordinates, initialZoomLevel) };

  return new MapInstance(wrapper, options);
};

export const getMarkerIcon = size => {
  const { Size } = window.google.maps;
  const icon = {
    url: 'https://static.andersenlab.com/andersenlab/new-andersensite/icons/map_marker.svg',
    scaledSize: new Size(25, 32),
  };
  const largeIcon = {
    url: 'https://static.andersenlab.com/andersenlab/new-andersensite/icons/map_marker.svg',
    scaledSize: new Size(40, 50),
  };

  if (size === 'large') {
    return largeIcon;
  }

  return icon;
};

export const createMarker = (office, setSelectedOffice, setCoordinates) => {
  if (!office) return null;

  const { Marker } = window.google.maps;

  const marker = new Marker({
    position: { lat: Number(office.coordinates[0]), lng: Number(office.coordinates[1]) },
    icon: getMarkerIcon(),
    city: office.city,
    hideOnZoomOut: office.hideOnZoomOut,
  });

  marker.addListener('click', () => {
    setSelectedOffice(prev => {
      if (prev && prev.office === office) return null;
      return {
        ...prev,
        office,
        hide: false,
        firstRender: false,
      };
    });

    setCoordinates(getCoordinatesWithOffset(office.coordinates, office.latOffset));
  });

  return marker;
};

export const getCoordinatesWithOffset = (coordinates, latOffset) => {
  const lat = Number(coordinates[0]) + latOffset;
  const lng = Number(coordinates[1]);

  return [lat, lng];
};

export const useFetchOfficesIds = () => {
  const OFFICES_IDS_STORAGE_KEY = 'numbers_array';
  const lang = useLang();
  const [officesIds, setOfficesIds] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOfficesIds = async () => {
    const cachedIds = JSON.parse(sessionStorage.getItem(OFFICES_IDS_STORAGE_KEY));
    const countryCode = getCountryCode() || lang;
    const queryParam =
      lang === 'de' || lang === 'uk' || lang === 'kz' || lang === 'pl' || lang === 'ae'
        ? lang
        : countryCode;

    if (!cachedIds) {
      await Api.getOfficesIds(queryParam)
        .then(data => {
          setOfficesIds(data);
          sessionStorage.setItem(OFFICES_IDS_STORAGE_KEY, JSON.stringify(data));
        })
        .finally(() => setLoading(false));
    } else {
      setOfficesIds(cachedIds);
      setLoading(false);
    }
  };

  const { inViewRef } = useDefferedCallback(fetchOfficesIds, {
    rootMargin: '20% 0% 20% 0%',
    delay: 3000,
  });

  return {
    inViewRef,
    officesIds,
    loading,
  };
};
